import { createFileRoute } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MultipleSelector,
  Skeleton,
} from '@org/ui';
import type { Option } from '@org/ui';
import { Video } from 'lucide-react';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useTeacherId } from '../components/teacher-picker';
import { EventFilters } from '../components/event-filters';
import { ReshuffleDialog } from '../components/reshuffle-dialog';

export const Route = createFileRoute('/next-classes')({
  component: NextClassesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

function extractFirstUrl(raw?: string | null) {
  if (!raw) return null;
  const match = raw.match(/https?:\/\/[^\s)]+/i);
  if (match?.[0]) return match[0];
  const meetMatch = raw.match(/(?:^|\s)(meet\.google\.com\/[^\s)]+)/i);
  if (meetMatch?.[1]) return `https://${meetMatch[1]}`;
  return null;
}

function formatParticipantLabel(participants: string[]) {
  if (participants.length <= 2) {
    return participants.join(', ');
  }
  const [first, second, ...rest] = participants;
  return `${first}, ${second} +${rest.length} more`;
}

function EventClassSelector({
  eventId,
  initialClasses,
  teacherId,
  seriesTemplateId,
  startTime,
  eventDate,
}: {
  eventId: string;
  initialClasses: Array<{ classId: string; className: string | null; classDescription: string | null }>;
  teacherId?: string;
  seriesTemplateId?: string | null;
  startTime?: Date | string | null;
  eventDate: string;
}) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const attachMutation = trpc.teacher.events.attachClasses.useMutation({
    onSuccess: () => {
      utils.teacher.events.next.invalidate();
      utils.teacher.events.past.invalidate();
    },
  });

  const shiftMutation = trpc.events.shiftSeriesClassesAfter.useMutation({
    onSuccess: (data) => {
      utils.teacher.events.next.invalidate();
      utils.teacher.events.past.invalidate();
      toast.success(
        t('teacher.reshuffle.successToast', { count: (data as any).shiftedCount ?? 0 }),
      );
      if ((data as any).droppedClassId) {
        toast.warning(t('teacher.reshuffle.droppedToast'));
      }
    },
  });

  const initialOptions: Option[] = initialClasses.map(c => ({
    value: c.classId,
    label: c.className ?? 'Unnamed',
  }));

  const [selected, setSelected] = useState<Option[]>(initialOptions);
  const [reshuffleState, setReshuffleState] = useState<{
    open: boolean;
    pendingSelection: Option[];
    insertClassId: string;
    newClassName: string;
  } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFutureSeriesEvent = useMemo(() => {
    if (!seriesTemplateId || !startTime) return false;
    return new Date(startTime).getTime() > Date.now();
  }, [seriesTemplateId, startTime]);

  const handleChange = useCallback((newSelected: Option[]) => {
    // Compute the new class ids (ones not present in initialClasses)
    const initialIds = new Set(initialClasses.map((c) => c.classId));
    const newClassIds = newSelected.filter((opt) => !initialIds.has(opt.value));
    const hasInitialClasses = initialClasses.length > 0;

    // Trigger the reshuffle dialog when:
    //   - the event is part of a recurring series,
    //   - the event is in the future,
    //   - the event already has at least one class assigned (i.e. we're
    //     replacing/reshuffling mid-series),
    //   - AND the teacher is adding a class that wasn't already attached.
    if (
      isFutureSeriesEvent &&
      hasInitialClasses &&
      newClassIds.length > 0
    ) {
      const insertOption = newClassIds[0];
      setReshuffleState({
        open: true,
        pendingSelection: newSelected,
        insertClassId: insertOption.value,
        newClassName: insertOption.label ?? 'Unnamed',
      });
      return;
    }

    // Default flow: debounced attach mutation, same as before.
    setSelected(newSelected);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      attachMutation.mutate({
        teacherId,
        eventId,
        classIds: newSelected.map(o => o.value),
      });
    }, 1000);
  }, [eventId, teacherId, attachMutation, initialClasses, isFutureSeriesEvent]);

  const handleReshuffleConfirm = () => {
    if (!reshuffleState) return;
    shiftMutation.mutate(
      {
        eventId,
        insertClassId: reshuffleState.insertClassId,
      },
      {
        onSuccess: () => {
          setSelected(reshuffleState.pendingSelection);
          setReshuffleState(null);
        },
        onError: (error) => {
          toast.error(error?.message ?? 'Failed to reshuffle classes');
          setReshuffleState(null);
        },
      },
    );
  };

  const handleReshuffleCancel = () => {
    // Restore prior selection.
    setSelected(initialOptions);
    setReshuffleState(null);
  };

  const handleSearch = useCallback(async (query: string): Promise<Option[]> => {
    if (query.length < 3) return [];
    const results = await utils.teacher.events.searchClasses.fetch({
      query,
      teacherId,
    });
    return results.map(c => ({
      value: c.classId,
      label: c.name ?? 'Unnamed',
    }));
  }, [teacherId, utils]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
        {t('teacher.classSelector.label')}
      </p>
      <MultipleSelector
        value={selected}
        onChange={handleChange}
        onSearch={handleSearch}
        delay={300}
        placeholder={t('teacher.classSelector.placeholder')}
        emptyIndicator={
          <p className="text-center text-sm text-gray-500">{t('teacher.classSelector.empty')}</p>
        }
        loadingIndicator={
          <p className="text-center text-sm text-gray-400">{t('common.status.searching')}</p>
        }
        commandProps={{ shouldFilter: false }}
      />
      {(attachMutation.isPending || shiftMutation.isPending) && (
        <p className="text-xs text-gray-400">{t('common.status.saving')}</p>
      )}
      {reshuffleState && (
        <ReshuffleDialog
          open={reshuffleState.open}
          onOpenChange={(o) => {
            if (!o) handleReshuffleCancel();
          }}
          eventDate={eventDate}
          newClassName={reshuffleState.newClassName}
          onConfirm={handleReshuffleConfirm}
          onCancel={handleReshuffleCancel}
          isLoading={shiftMutation.isPending}
        />
      )}
    </div>
  );
}

function NextClassesPage() {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const { data, isLoading } = trpc.teacher.events.next.useQuery({ teacherId });
  const events = data?.events ?? [];

  const [groupId, setGroupId] = useState<string>('all');
  const [studentSearch, setStudentSearch] = useState<string>('');

  const groupOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const event of events) {
      for (const group of event.groups) {
        if (!seen.has(group.groupId)) {
          seen.set(group.groupId, group.label);
        }
      }
    }
    return Array.from(seen, ([gId, label]) => ({ groupId: gId, label }));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const search = studentSearch.toLowerCase().trim();
    return events.filter((event) => {
      if (groupId !== 'all' && !event.groups.some((g) => g.groupId === groupId)) return false;
      if (search && !event.participants.some((p) => p.toLowerCase().includes(search))) return false;
      return true;
    });
  }, [events, groupId, studentSearch]);

  const kindLabel = (eventKind?: string | null) =>
    eventKind
      ? t(`common.eventKinds.${eventKind}`, {
          defaultValue: eventKind.replace('-', ' '),
        })
      : t('common.eventKinds.default');

  const participantHeading = t('teacher.nextClassesPage.participantsHeading');
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">
            {t('teacher.nextClassesPage.eyebrow')}
          </p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.nextClassesPage.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.nextClassesPage.description')}</p>
        </div>
        <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
          {t('teacher.nextClassesPage.badgeLabel', { count: filteredEvents.length })}
        </Badge>
      </div>

      {!isLoading && groupOptions.length > 0 && (
        <EventFilters
          groupId={groupId}
          onGroupChange={setGroupId}
          groups={groupOptions}
          studentSearch={studentSearch}
          onStudentSearchChange={setStudentSearch}
        />
      )}

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="border border-rose-50 bg-white">
              <CardHeader className="space-y-3">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && filteredEvents.length === 0 && (
        <Card className="border border-dashed border-rose-200 bg-white/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{t('teacher.nextClassesPage.emptyTitle')}</CardTitle>
            <CardDescription>{t('teacher.nextClassesPage.emptyDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {filteredEvents.map((event) => {
          const eventKindLabel = kindLabel(event.eventKind ?? undefined);
          const startLabel = event.startTime ? dateFormatter.format(new Date(event.startTime)) : t('common.fallbacks.dateNotProvided');
          const locationUrl = extractFirstUrl(event.location);
          const statusLabel = (event.status ?? '').replace('-', ' ') || t('teacher.nextClassesPage.statusFallback');
          return (
            <Card
              key={event.eventId}
              className="border border-rose-50 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <CardHeader className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <Badge className="rounded-full border border-rose-100 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#cf2e2e]">
                        {eventKindLabel}
                      </Badge>
                      <span className="text-xs uppercase tracking-[0.3em] text-gray-400">{statusLabel}</span>
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-gray-900">
                        {event.title ?? t('common.fallbacks.untitledClass')}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-600">{startLabel}</CardDescription>
                    </div>
                  </div>
                  {locationUrl ? (
                    <Button
                      asChild
                      variant="ghost"
                      className="h-12 w-12 rounded-full border border-rose-100 bg-white text-[#cf2e2e] shadow-sm transition hover:border-rose-200 hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-rose-200"
                    >
                      <a
                        href={locationUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t('teacher.nextClassesPage.joinLabel')}
                      >
                        <Video className="h-5 w-5" aria-hidden />
                        <span className="sr-only">{t('teacher.nextClassesPage.joinLabel')}</span>
                      </a>
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {event.groups.length ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {event.groups.map((group) => (
                      <Badge
                        key={`${event.eventId}-${group.groupId}`}
                        className="rounded-full border border-rose-100 bg-[#fff7f9] px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#cf2e2e]"
                      >
                        {group.label}
                      </Badge>
                    ))}
                  </div>
                ) : null}

                {['private-class', 'tutoring'].includes(event.eventKind ?? '') && event.participants.length ? (
                  <div className="rounded-2xl border border-dashed border-rose-100 bg-white/80 p-4 text-sm text-gray-600">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400">{participantHeading}</p>
                    <p className="font-semibold text-gray-900">{formatParticipantLabel(event.participants)}</p>
                  </div>
                ) : null}

                <EventClassSelector
                  eventId={event.eventId}
                  initialClasses={event.classes}
                  teacherId={teacherId}
                  seriesTemplateId={(event as any).seriesTemplateId ?? null}
                  startTime={event.startTime ?? null}
                  eventDate={startLabel}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
